import { SimpleSelect } from '@chronosphereio/chrono-ui';
import constate from 'constate';
import { produce } from 'immer';
import { useState } from 'react';
import { VariableStateMap, VariableValue } from '@/core';
import { TemplateVariableContextProvider } from '@/plugin-system';
import { useSplitVariableValues } from './split-mode-model';

function useInternalSplitVariableState({
  variableName,
  initialValue,
}: {
  variableName: string | null;
  initialValue?: string | null;
}) {
  const { splitVariableValues, processedVariableState } = useSplitVariableValues({ variableName: variableName ?? '' });
  const [selectedValue, setSelectedValue] = useState<string | null>(initialValue ?? splitVariableValues[0] ?? null);
  return { selectedValue, setSelectedValue, splitVariableValues, variableName, processedVariableState };
}

const [SplitVariableProviderInternal, useSplitVariableState] = constate(
  (props: ReturnType<typeof useInternalSplitVariableState>) => {
    return props;
  }
);

function SelectVariable() {
  const { splitVariableValues, selectedValue, setSelectedValue, variableName } = useSplitVariableState();
  if (!variableName) {
    return null;
  }
  return (
    <SimpleSelect
      label={variableName}
      options={splitVariableValues.map((v) => ({ key: v, label: v }))}
      value={selectedValue ?? ''}
      onChange={(e) => {
        setSelectedValue(e.target.value);
      }}
    />
  );
}

function Provider(props: { children?: React.ReactNode; variableName: string | null; initialValue?: string | null }) {
  const state = useInternalSplitVariableState({
    variableName: props.variableName,
    initialValue: props.initialValue,
  });
  const { variableName, processedVariableState, selectedValue: variableValue } = state;
  if (!variableName) {
    return <>{props.children}</>;
  }

  return (
    <SplitVariableTemplateVariableModifierProvider
      variableState={processedVariableState}
      variableName={variableName}
      variableValue={variableValue}
    >
      <SplitVariableProviderInternal {...state}>{props.children}</SplitVariableProviderInternal>
    </SplitVariableTemplateVariableModifierProvider>
  );
}

export const SplitVariable = {
  Provider,
  SelectVariable,
};

export function SplitVariableIterator(props: {
  variableName: string;
  renderItem: (variableName: string, payload: { idx: number; totalSelected: number }) => React.ReactNode;
}) {
  const { variableName } = props;
  const { splitVariableValues, processedVariableState } = useSplitVariableValues({ variableName });

  return (
    <>
      {splitVariableValues.map((option, idx) => {
        return (
          <SplitVariableTemplateVariableModifierProvider
            key={variableName + option}
            variableName={variableName}
            variableValue={option}
            variableState={processedVariableState}
          >
            {props.renderItem(option, { totalSelected: splitVariableValues.length, idx })}
          </SplitVariableTemplateVariableModifierProvider>
        );
      })}
    </>
  );
}

function SplitVariableTemplateVariableModifierProvider(props: {
  variableName: string;
  variableValue: VariableValue;
  children?: React.ReactNode;
  variableState: VariableStateMap;
}) {
  const { variableName, variableState, variableValue } = props;
  const modifiedState = produce(variableState, (draft: VariableStateMap) => {
    // Inject split variable with new value
    draft[variableName] = {
      loading: false,
      value: variableValue,
    };
  });
  return (
    <TemplateVariableContextProvider
      value={{
        state: modifiedState,
      }}
    >
      {props.children}
    </TemplateVariableContextProvider>
  );
}
